import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Ability,
  AbilityEffect, 
  AttachedTable, 
  ConsumedResource, 
  UpgradableNumber,
  WEAPON_KEYWORD_OPTIONS,
} from "@dndModels";
import { 
  DURATIONS,
  COMPONENTS,
  STANDARD_CASTING_TIMES
} from "@dndModels/Constants";
import { CastingTime, DurationType } from "@dndModels/Types";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import EffectsInput from "@dndComponents/feature/EffectsInput";
import ConsumedResourceInput from "@dndComponents/feature/ConsumedResourceInput";
import StringsBox from "@components/input/strings/StringsBox";
import { FeatureSource } from '@models';

interface Props {
  obj: Ability;
  feature_source?: FeatureSource;
  onChange: (changed: Ability) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function AbilityInput({
  obj,
  feature_source,
  disabled,
  attached_table,
  onChange,
}: Props) {
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  // const [show_effect, setShowEffect] = useState("");

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderCountsAsWeaponMain = () => {
    if (obj.counts_as_weapon === "Use Properties") {
      return (
        <>
          { renderCountsAsWeapon() }
          { obj.counts_as_weapon === "Use Properties" &&
            <>
              <Grid
                size={{
                  xs: 4
                }}>
                <SelectStringBox 
                  name="Weapon Keywords" 
                  options={WEAPON_KEYWORD_OPTIONS}
                  values={ obj.weapon_properties.wk_ids } 
                  multiple
                  disabled={disabled}
                  onChange={(changed: string[]) => {
                    const new_obj = obj;
                    new_obj.weapon_properties.wk_ids = changed;
                    onChange(new_obj);
                    setReloading(true);
                  }} 
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <SelectObjectBox 
                  name="Weapon Mastery Property" 
                  data_type="weapon_mastery_property"
                  value={ obj.weapon_properties.wmp_id } 
                  disabled={disabled}
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    new_obj.weapon_properties.wmp_id = changed;
                    onChange(new_obj);
                    setReloading(true);
                  }} 
                />
              </Grid>
            </>
          }
        </>
      );
    } else {
      return renderCountsAsWeapon();
    }
  }

  const renderCountsAsWeapon = () => {
    return (
      <Grid
        size={{
          xs: obj.counts_as_weapon === "Use Properties" ? 4 : 12
        }}>
        <SelectObjectBox 
          name="Counts as Weapon"
          data_type="item"
          filter={{ item_type: "Weapon" }}
          extra_options={["None","Improvised","Use Properties"]}
          value={ obj.counts_as_weapon === "" ? "None" : obj.counts_as_weapon }
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.counts_as_weapon = changed === "None" ? "" : changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={12}>
          <StringsBox 
            name="Tags"
            values={ obj.tags }
            disabled={disabled}
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.tags = changed;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
        <Grid size={12}>
          <SelectStringBox 
            name="Special Action Type"
            options={["Attack","Ability","Spell","Item Action","None","Inherit"]}
            value={ obj.special_action_type }
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              (new_obj.special_action_type as string) = changed;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
        { renderCountsAsWeaponMain() }
        <Grid size={4}>
          <SelectStringBox 
            name="Casting Time"
            options={STANDARD_CASTING_TIMES}
            extra_options={["Special"]}
            value={obj.casting_time}
            disabled={disabled}
            onChange={(changed: CastingTime) => {
              const new_obj = obj;
              new_obj.casting_time = changed;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
        <Grid size={4}>
          <SelectStringBox 
            name="Components"
            options={COMPONENTS}
            values={obj.components}
            multiple
            disabled={disabled}
            onChange={(values: string[]) => {
              const new_obj = obj;
              new_obj.components = values;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
        <Grid size={4}>
          <CheckBox 
            name="Sight"
            value={obj.sight}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.sight = changed;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
        { obj.casting_time === "Special" && 
          <Grid size={12}>
            <StringBox 
              name="Special Casting Time"
              value={obj.special_casting_time}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.special_casting_time = changed;
                onChange(new_obj);
                setReloading(true);
              }}
            /> 
          </Grid>
        }
      </Grid>
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={6}>
          <UpgradableNumberBox 
            name="Attack Bonus"
            slot_level={1}
            value={obj.attack_bonus} 
            disabled={disabled}
            attached_table={attached_table}
            quick_options={["Attack STR","Attack DEX","Attack INT","Attack WIS","Attack CHA"]}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.attack_bonus = value;
              onChange(new_obj);
              setReloading(true);
            }}
          />
        </Grid>
        <Grid size={6}>
          <UpgradableNumberBox 
            name="DC"
            slot_level={1}
            value={obj.dc} 
            disabled={disabled}
            attached_table={attached_table}
            quick_options={["DC","DC STR","DC DEX","DC CON","DC INT","DC WIS","DC CHA","Prof"]}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.dc = value;
              onChange(new_obj);
              setReloading(true);
            }}
          />
        </Grid>
      </Grid>
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={6}
          container spacing={0} direction="row">
          <Grid
            size={{
              xs: obj.duration === "Instantaneous" ? 12 : 9
            }}>
            <SelectStringBox 
              name="Duration"
              options={DURATIONS}
              value={ !DURATIONS.includes(obj.duration) ? "Special" : obj.duration }
              disabled={disabled}
              onChange={(changed: DurationType) => {
                const new_obj = obj;
                new_obj.duration = changed;
                onChange(new_obj);
                setReloading(true);
              }}
            /> 
          </Grid>
          { obj.duration !== "Instantaneous" && 
            <Grid size={3}>
              <CheckBox 
                name="C" 
                value={obj.concentration} 
                disabled={disabled}
                onChange={(e: boolean) => {
                  const new_obj = obj;
                  new_obj.concentration = e;
                  onChange(new_obj);
                  setReloading(true);
                }} 
              />
            </Grid>
          } 
          { (obj.duration === "Special" || !DURATIONS.includes(obj.duration)) && 
            <Grid size={12}>
              <StringBox 
                name="Duration"
                value={obj.duration === "Special" ? "" : obj.duration}
                disabled={disabled}
                onBlur={(changed: DurationType) => {
                  const new_obj = obj;
                  new_obj.duration = changed;
                  onChange(new_obj);
                  setReloading(true);
                }}
              /> 
            </Grid>
          } 
        </Grid>
        <Grid size={6}>
          <SelectStringBox 
            name="Show In"
            options={["Actions","Inventory","Item","Source"]}
            values={obj.show_in}
            disabled={disabled}
            multiple
            onChange={(changed: ("Actions" | "Inventory" | "Item" | "Source")[]) => {
              const new_obj = obj;
              new_obj.show_in = changed;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
      </Grid>
      { obj.components.includes("M") &&
        <Grid size={12}>
          <StringBox 
            name="Material Component"
            value={obj.material_component}
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.material_component = value;
              onChange(new_obj);
              setReloading(true);
            }}
          /> 
        </Grid>
      }
      <Grid size={12}>
        <EffectsInput
          effects={obj.effects}
          disabled={disabled}
          for_obj={obj}
          feature_source={feature_source}
          attached_table={attached_table}
          onChange={(changed: AbilityEffect[]) => {
            const new_obj = obj;
            obj.effects = changed;
            // console.log(obj.effects);
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <ConsumedResourceInput 
          obj={obj.consumed_resource} 
          disabled={false}
          attached_table={attached_table}
          onChange={(changed: ConsumedResource) => {
            const new_obj = obj;
            new_obj.consumed_resource = changed;
            onChange(new_obj);
            setReloading(true);
          }}
        />
      </Grid>
    </Grid>
  );
}
